/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Divider,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

/**
 * Confirms converting a Disqualified lead into a Contact (no Deal).
 * Loads a field preview from the API so the user sees exactly which contact
 * object fields will be populated.
 */
function ConvertLeadToContactModal({
  open,
  onClose,
  onConfirm,
  leadId,
  axiosInstance,
  leadStatus,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !leadId || !axiosInstance) {
      setPreview(null);
      setError("");
      setConfirming(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axiosInstance.get(
          `/leads/${leadId}/convert-to-contact/preview`
        );
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setError(
            err?.response?.data?.detail ||
              "Could not load the contact field preview."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, leadId, axiosInstance]);

  const handleClose = () => {
    if (confirming) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);
    setError("");
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to convert lead to contact."
      );
    } finally {
      setConfirming(false);
    }
  };

  const fields = preview?.fields || [];

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="convert-contact-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 560 },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <PersonAddAlt1Icon sx={{ color: "white" }} />
          <MDTypography id="convert-contact-title" variant="h6" color="white">
            Convert lead to contact
          </MDTypography>
        </Box>

        <MDBox px={3} py={2.5} sx={{ overflowY: "auto", flex: 1 }}>
          <MDTypography variant="body2" color="text" mb={1.5}>
            This {leadStatus ? `${leadStatus.toLowerCase()} ` : ""}lead will be
            converted into a <strong>contact</strong>.
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            The fields below will be added on the contact record Empty values are shown as a dash.
          </MDTypography>

          {loading ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </MDBox>
          ) : error && !preview ? (
            <MDTypography variant="body2" color="error">
              {error}
            </MDTypography>
          ) : (
            <TableContainer
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                maxHeight: 360,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: "40%" }}>
                      Contact field
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Value from lead</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((row) => (
                    <TableRow key={row.field} hover>
                      <TableCell>
                        <MDTypography variant="caption" fontWeight="medium">
                          {row.label}
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography
                          variant="caption"
                          color={row.value ? "text" : "secondary"}
                          sx={{ wordBreak: "break-word" }}
                        >
                          {row.value || "—"}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {error && preview ? (
            <MDTypography variant="caption" color="error" display="block" mt={2}>
              {error}
            </MDTypography>
          ) : null}
        </MDBox>

        <Divider />
        <MDBox display="flex" justifyContent="flex-end" gap={1.5} px={3} py={2}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={confirming}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleConfirm}
            disabled={loading || confirming || !preview}
          >
            {confirming ? (
              <>
                <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                Converting…
              </>
            ) : (
              "Convert to contact"
            )}
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );
}

ConvertLeadToContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  axiosInstance: PropTypes.object,
  leadStatus: PropTypes.string,
};

export default ConvertLeadToContactModal;
